import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Aperture } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr("");
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) navigate("/");
        else setErr(res.error);
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            {/* Left: editorial photo */}
            <div className="hidden lg:block relative bg-gray-900 overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/19222069/pexels-photo-19222069.jpeg"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-80 editorial-hero"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-900/80"></div>
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-md bg-blue-500 flex items-center justify-center">
                            <Aperture className="w-5 h-5" strokeWidth={2.2} />
                        </div>
                        <div>
                            <div className="font-serif text-lg leading-none">Groupfie</div>
                            <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
                                Studio CRM
                            </div>
                        </div>
                    </div>
                    <div>
                        <h1 className="font-serif text-4xl leading-tight max-w-md">
                            Run every shoot, every client, every payment — from one refined workspace.
                        </h1>
                        <p className="text-sm text-white/70 mt-4 max-w-md">
                            Track projects from enquiry to delivery, assign photographers, manage payments and
                            calculate margins in real time.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-8 lg:p-16">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="mb-8">
                        <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Welcome back</div>
                        <h2 className="font-serif text-3xl text-gray-900">Sign in to Groupfie</h2>

                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                required
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                required
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="mt-1"
                            />
                        </div>
                        {err && (
                            <div
                                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                                data-testid="login-error"
                            >
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            className="w-full bg-blue-500 hover:bg-blue-600"
                            disabled={loading}
                            data-testid="login-submit-button"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </Button>
                    </div>

                    {/* <div className="mt-8 text-xs text-gray-400 border-t border-gray-100 pt-4">
                        Demo credentials: <span className="tabular-nums">admin@studio.com</span> · admin123
                    </div> */}
                </form>
            </div>
        </div>
    );
}
